import { Controller } from "@hotwired/stimulus"

// Row selection for the Data Management grid.
//
// The grid renders at most one run's worth of rows (WorkspacesController caps
// it at the entity's selection_cap), so the selection is always an explicit
// Set of record ids and checking the header box selects everything a run can
// touch. There is no page-two, and so no "select all matching" escalation and
// no client-side cap to enforce — SelectionResolver still re-checks the cap
// server-side at preview time.
export default class extends Controller {
  static targets = ["checkbox", "selectAll", "countLabel", "bulkEditButton", "bulkPanel", "mixedNotice"]
  // Single-entity grids pass bulkFormUrl + frameId (dm_bulk_form_<entity>).
  // The merged Products grid passes bulkFormUrls ({entity: url}) instead and
  // tags each checkbox with data-entity: rows of both kinds share one table,
  // so the endpoint and frame id are resolved from the selection when the
  // bulk form opens — and a selection mixing entities cannot open it at all.
  static values = { bulkFormUrl: String, frameId: String, bulkFormUrls: Object }

  // State lives in initialize(): targetConnected callbacks fire before
  // connect(), so anything they touch must exist by then.
  initialize() {
    this.selectedIds = new Set()
    this.entityById = new Map()
    this.lastClickedCheckbox = null
  }

  connect() {
    // Not capture-phase: a capturing listener swallowed Escape before
    // TomSelect could close its dropdown with it.
    this.boundHandleEscape = this.handleEscape.bind(this)
    document.addEventListener("keydown", this.boundHandleEscape)

    // Sorting or re-running filters is a full navigation, which throws the
    // selection away. Warn first.
    this.boundGuardVisit = this.guardVisit.bind(this)
    this.boundGuardSubmit = this.guardSubmit.bind(this)
    document.addEventListener("turbo:before-visit", this.boundGuardVisit)
    document.addEventListener("turbo:submit-start", this.boundGuardSubmit)

    this.updateUI()
  }

  disconnect() {
    document.removeEventListener("keydown", this.boundHandleEscape)
    document.removeEventListener("turbo:before-visit", this.boundGuardVisit)
    document.removeEventListener("turbo:submit-start", this.boundGuardSubmit)
  }

  guardVisit(event) {
    if (this.confirmDiscard()) return

    event.preventDefault()
  }

  guardSubmit(event) {
    // The selection POST is the one submission that carries the selection with
    // it, so warning about losing it would be nonsense.
    if (event.target?.dataset?.dmSelectionForm) return
    // The bulk form lives in the modal and is exempt (see confirmDiscard).
    if (this.confirmDiscard()) return

    event.detail.formSubmission?.stop()
  }

  // True when it's fine to proceed: nothing selected, or the user accepted
  // losing it. The bulk modal is exempt because its own forms (preview,
  // confirm, cancel, undo) submit from inside it.
  confirmDiscard() {
    if (this.selectedIds.size === 0) return true
    if (this.bulkModalOpen()) return true

    return window.confirm(
      `Leaving this view clears your selection of ${this.selectedIds.size} records. Continue?`
    )
  }

  checkboxTargetConnected(checkbox) {
    checkbox.checked = this.selectedIds.has(checkbox.dataset.recordId)
    if (this.selectedIds.size > 0) this.updateUI()
  }

  toggleCheckbox(event) {
    const checkbox = event.currentTarget

    if (event.shiftKey && this.lastClickedCheckbox && this.lastClickedCheckbox !== checkbox) {
      this.applyRange(this.lastClickedCheckbox, checkbox)
    } else if (checkbox.checked) {
      this.select(checkbox)
    } else {
      this.deselect(checkbox)
    }

    this.lastClickedCheckbox = checkbox
    this.updateUI()
  }

  applyRange(fromCheckbox, toCheckbox) {
    const boxes = this.visibleCheckboxes()
    const fromIndex = boxes.indexOf(fromCheckbox)
    const toIndex = boxes.indexOf(toCheckbox)
    if (fromIndex === -1 || toIndex === -1) return

    const shouldCheck = toCheckbox.checked
    const [start, end] = [Math.min(fromIndex, toIndex), Math.max(fromIndex, toIndex)]

    for (let i = start; i <= end; i++) {
      if (shouldCheck) {
        this.select(boxes[i])
      } else {
        this.deselect(boxes[i])
      }
    }
  }

  toggleAll(event) {
    if (!event.currentTarget.checked) {
      this.clear()
      return
    }

    this.visibleCheckboxes().forEach(box => this.select(box))
    this.updateUI()
  }

  select(box) {
    box.checked = true
    this.selectedIds.add(box.dataset.recordId)
    if (box.dataset.entity) this.entityById.set(box.dataset.recordId, box.dataset.entity)
  }

  deselect(box) {
    box.checked = false
    this.selectedIds.delete(box.dataset.recordId)
    this.entityById.delete(box.dataset.recordId)
  }

  clear() {
    this.selectedIds.clear()
    this.entityById.clear()
    this.lastClickedCheckbox = null
    this.checkboxTargets.forEach(box => { box.checked = false })
    this.closeBulkForm()
    this.updateUI()
  }

  // Load the bulk-operation form into the centered modal, carrying the
  // selected ids.
  //
  // Posted rather than hung off a URL. A few hundred selected ids exceed the
  // query-string limit (Puma caps it at 10KB, proxies often lower), which used
  // to reject the frame load and open an empty modal with no explanation.
  openBulkForm(event) {
    event?.preventDefault()
    if (!this.hasBulkPanelTarget) return

    const target = this.bulkFormTarget()
    if (!target) return

    const form = document.createElement("form")
    form.method = "post"
    form.action = target.url
    form.hidden = true
    form.dataset.turboFrame = target.frameId
    // Exempts this from the leave-guard below: it carries the selection rather
    // than discarding it.
    form.dataset.dmSelectionForm = "true"

    const token = document.querySelector("meta[name='csrf-token']")?.content
    if (token) form.append(this.hiddenField("authenticity_token", token))

    this.selectedIds.forEach(id => form.append(this.hiddenField("ids[]", id)))

    // Detached only once the submission is done. Removing it in the same tick
    // left Turbo unable to resolve the target frame from a form that was no
    // longer in the document, so it fell back to a full-page visit — which the
    // leave-guard then blocked, leaving an empty modal.
    this.element.querySelectorAll("form[data-dm-selection-form]").forEach(stale => stale.remove())
    this.element.append(form)
    form.addEventListener("turbo:submit-end", () => form.remove(), { once: true })
    form.requestSubmit()

    window.bootstrap.Modal.getOrCreateInstance(this.bulkPanelTarget).show()
  }

  // Where the bulk form posts and which frame answers. On the merged grid
  // the entity comes from the selection, and the modal's frame is renamed to
  // match before the request so the per-entity response can find it.
  bulkFormTarget() {
    if (!this.multiEntity()) {
      return this.hasBulkFormUrlValue && this.bulkFormUrlValue !== ""
        ? { url: this.bulkFormUrlValue, frameId: this.frameIdValue }
        : null
    }

    const entities = [...this.selectedEntities()]
    if (entities.length !== 1) return null

    const url = this.bulkFormUrlsValue[entities[0]]
    if (!url) return null

    const frameId = `dm_bulk_form_${entities[0]}`
    const frame = this.bulkPanelTarget.querySelector("turbo-frame")
    if (frame) frame.id = frameId

    return { url, frameId }
  }

  multiEntity() {
    return Object.keys(this.bulkFormUrlsValue).length > 0
  }

  selectedEntities() {
    const entities = new Set()
    this.selectedIds.forEach(id => entities.add(this.entityById.get(id)))
    return entities
  }

  hiddenField(name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    return input
  }

  closeBulkForm(event) {
    event?.preventDefault()
    if (!this.hasBulkPanelTarget) return

    window.bootstrap.Modal.getInstance(this.bulkPanelTarget)?.hide()
    this.clearBulkFrame()
  }

  // Dismisses a finished run and reloads the grid at the current URL, which
  // still carries the filters. Without the reload the rows behind the panel
  // keep showing the values the run just replaced, which reads as a failure
  // and invites a duplicate run. clear() first, so the leave-guard doesn't
  // warn about a selection that has already been applied.
  finishRun(event) {
    event?.preventDefault()
    this.clear()
    window.Turbo.visit(window.location.href, { action: "replace" })
  }

  // Dismissals that bypass closeBulkForm (backdrop click, the modal's own
  // Escape handling) also discard the loaded form, so reopening always
  // reflects the current selection. A successful Cancel POST empties the
  // frame server-side, so it hides the modal too.
  bulkPanelTargetConnected(modal) {
    // A hide's transition can finish after a newer open has already filled
    // the frame (throttled/background tabs stretch transitions), so only
    // clear when the modal really ended up closed.
    this.boundClearBulkFrame = () => {
      if (this.bulkModalOpen()) return

      this.clearBulkFrame()
    }
    modal.addEventListener("hidden.bs.modal", this.boundClearBulkFrame)

    // Content can also arrive without openBulkForm — the audit page's Revert
    // button posts straight into the frame — so show the modal whenever the
    // frame gains content. Already-open is a no-op.
    this.boundShowOnFrameLoad = (event) => {
      // Bound on the modal, so any frame the event bubbles from is ours —
      // the id varies by entity on the merged grid.
      if (event.target.tagName !== "TURBO-FRAME") return
      if (event.target.innerHTML.trim() === "") return

      window.bootstrap.Modal.getOrCreateInstance(modal).show()
    }
    modal.addEventListener("turbo:frame-load", this.boundShowOnFrameLoad)

    this.boundHandleBulkSubmitEnd = (event) => {
      const cancelled = event.target.action?.includes("/cancel")
      if (cancelled && event.detail.success) {
        window.bootstrap.Modal.getInstance(modal)?.hide()
      }
    }
    modal.addEventListener("turbo:submit-end", this.boundHandleBulkSubmitEnd)
  }

  bulkPanelTargetDisconnected(modal) {
    if (this.boundClearBulkFrame) modal.removeEventListener("hidden.bs.modal", this.boundClearBulkFrame)
    if (this.boundHandleBulkSubmitEnd) modal.removeEventListener("turbo:submit-end", this.boundHandleBulkSubmitEnd)
    if (this.boundShowOnFrameLoad) modal.removeEventListener("turbo:frame-load", this.boundShowOnFrameLoad)
  }

  clearBulkFrame() {
    if (!this.hasBulkPanelTarget) return

    const frame = this.bulkPanelTarget.querySelector("turbo-frame")
    frame.removeAttribute("src")
    frame.innerHTML = ""
  }

  handleEscape(event) {
    if (event.key !== "Escape") return

    // With the bulk modal open, Escape only dismisses the modal (Bootstrap
    // handles the hide); the selection survives for another attempt.
    if (this.bulkModalOpen()) return
    if (this.selectedIds.size === 0) return
    // Escape belongs to whatever the user is actually interacting with: an
    // open dropdown, or a field they're typing in.
    if (this.escapeBelongsElsewhere(event)) return

    this.clear()
  }

  escapeBelongsElsewhere(event) {
    if (document.querySelector(".ts-wrapper.dropdown-active")) return true
    if (event.target.closest(".ts-wrapper, .modal, input, select, textarea")) return true

    return false
  }

  bulkModalOpen() {
    return this.hasBulkPanelTarget && this.bulkPanelTarget.classList.contains("show")
  }

  visibleCheckboxes() {
    return this.checkboxTargets.filter(box => box.offsetParent !== null)
  }

  updateUI() {
    this.updateCountLabel()
    this.updateSelectAll()
    this.updateBulkEditButton()
  }

  updateBulkEditButton() {
    if (!this.hasBulkEditButtonTarget) return

    const hasSelection = this.selectedIds.size > 0
    this.bulkEditButtonTarget.classList.toggle("d-none", !hasSelection)
    if (!hasSelection) this.closeBulkForm()

    // A bulk action runs on one entity, so a selection spanning both kinds
    // of product has no single form to open.
    const mixed = hasSelection && this.multiEntity() && this.selectedEntities().size > 1
    this.bulkEditButtonTarget.toggleAttribute("disabled", mixed)
    if (this.hasMixedNoticeTarget) this.mixedNoticeTarget.classList.toggle("d-none", !mixed)
  }

  updateCountLabel() {
    if (!this.hasCountLabelTarget) return

    this.countLabelTarget.textContent = `${this.selectedIds.size} selected`
  }

  updateSelectAll() {
    if (!this.hasSelectAllTarget) return

    const boxes = this.visibleCheckboxes()
    const checked = boxes.filter(box => box.checked)

    if (checked.length === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checked.length === boxes.length) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }
}
